/**
 * Tiny line + area chart for time-series. Generic on x — caller passes
 * pre-labeled points and we just plot index → y. Doesn't pretend to be
 * a real date-axis chart; for our use cases (5-50 points) that's plenty.
 *
 * Y axis auto-scales to the data unless `yMax` is passed.
 */

interface Point {
  /** Display label for the point — shown in tooltip on hover. */
  label: string;
  /** Numeric value. */
  y: number;
}

/**
 * "Nice" interior gridline values between min and max, so the Y labels are
 * round numbers that sit *exactly* on their lines. The old approach put lines
 * at 25/50/75 % of the range — for a range like 33 that's 8.25 / 16.5 / 24.75,
 * which `toFixed(0)` then rounded to 8 / 17 / 25, no longer matching where the
 * line was actually drawn. Picking a round step (…, 1, 2, 2.5, 5, 10, …) keeps
 * label text and line position in sync.
 */
function niceGridValues(min: number, max: number, target = 4): number[] {
  const span = max - min;
  if (span <= 0) return [];
  const rawStep = span / target;
  const mag = 10 ** Math.floor(Math.log10(rawStep));
  const step =
    [1, 2, 2.5, 5, 10].map((m) => m * mag).find((s) => s >= rawStep) ?? 10 * mag;
  const out: number[] = [];
  for (let v = Math.ceil(min / step) * step; v < max - step * 1e-6; v += step) {
    if (v > min + step * 1e-6) out.push(v);
  }
  return out;
}

/** Format a gridline value exactly (no rounding that would un-align the label). */
function formatTick(v: number): string {
  return Number.isInteger(v) ? String(v) : parseFloat(v.toFixed(2)).toString();
}

interface Props {
  points: readonly Point[];
  yMax?: number;
  yMin?: number;
  height?: number;
  /** Show 25/50/75/100 gridlines (vs no gridlines). */
  showGrid?: boolean;
  /** Stroke + fill color, hex / tailwind rgb format. */
  color?: string;
}

export function LineChart({
  points,
  yMax,
  yMin = 0,
  height = 140,
  showGrid = true,
  color = 'rgb(56 189 248)',
}: Props): JSX.Element {
  if (points.length === 0) {
    return (
      <div
        className="w-full flex items-center justify-center text-xs text-slate-500"
        style={{ height }}
      >
        暂无数据
      </div>
    );
  }

  const w = 600; // intrinsic width; SVG scales
  const padL = 28;
  const padR = 12;
  const padT = 10;
  const padB = 22;
  const innerW = w - padL - padR;
  const innerH = height - padT - padB;

  const dataMax = yMax ?? Math.max(...points.map((p) => p.y), 1);
  const range = Math.max(dataMax - yMin, 1);

  const xOf = (i: number): number =>
    padL + (points.length === 1 ? innerW / 2 : (i / (points.length - 1)) * innerW);
  const yOf = (v: number): number =>
    padT + innerH - ((v - yMin) / range) * innerH;

  const linePath = points
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${xOf(i).toFixed(1)} ${yOf(p.y).toFixed(1)}`)
    .join(' ');

  const areaPath =
    `M ${xOf(0).toFixed(1)} ${(padT + innerH).toFixed(1)} ` +
    points.map((p, i) => `L ${xOf(i).toFixed(1)} ${yOf(p.y).toFixed(1)}`).join(' ') +
    ` L ${xOf(points.length - 1).toFixed(1)} ${(padT + innerH).toFixed(1)} Z`;

  // Pick 3-4 evenly-spaced x ticks so the label row doesn't crowd.
  const tickEvery = Math.max(1, Math.ceil(points.length / 5));

  // Round gridline values so labels land exactly on their lines (see helper).
  const gridValues = showGrid ? niceGridValues(yMin, dataMax) : [];

  return (
    <svg
      viewBox={`0 0 ${w} ${height}`}
      className="w-full h-auto"
      preserveAspectRatio="none"
      role="img"
      aria-label="折线图"
    >
      {/* Gridlines + y labels */}
      {gridValues.map((g, idx) => (
        <g key={idx}>
          <line
            x1={padL}
            x2={w - padR}
            y1={yOf(g)}
            y2={yOf(g)}
            stroke="rgb(51 65 85)"
            strokeOpacity={0.4}
            strokeDasharray="3 3"
          />
          <text
            x={padL - 4}
            y={yOf(g)}
            fontSize={9}
            fill="rgb(100 116 139)"
            textAnchor="end"
            dominantBaseline="middle"
          >
            {formatTick(g)}
          </text>
        </g>
      ))}

      {/* Baseline */}
      <line
        x1={padL}
        x2={w - padR}
        y1={padT + innerH}
        y2={padT + innerH}
        stroke="rgb(51 65 85)"
        strokeWidth={1}
      />

      {/* Area fill */}
      <path d={areaPath} fill={color} fillOpacity={0.15} />
      {/* Line */}
      <path d={linePath} fill="none" stroke={color} strokeWidth={2} />
      {/* Points */}
      {points.map((p, i) => (
        <g key={i}>
          <circle cx={xOf(i)} cy={yOf(p.y)} r={3} fill={color} />
          <title>{`${p.label}: ${p.y.toFixed(1)}`}</title>
        </g>
      ))}

      {/* X labels (sparse) */}
      {points.map((p, i) => {
        if (i % tickEvery !== 0 && i !== points.length - 1) return null;
        return (
          <text
            key={i}
            x={xOf(i)}
            y={height - 6}
            fontSize={9}
            fill="rgb(100 116 139)"
            textAnchor="middle"
          >
            {p.label}
          </text>
        );
      })}
    </svg>
  );
}
